import * as React from "react";
import { api } from "./api";
import { HomePage } from "./HomePage";
import { ProfilePage } from "./ProfilePage";
import { RoomPage } from "./RoomPage";
import { SpotifyImportPage } from "./SpotifyImportPage";
import { AppLink, NavigationProvider, useNavigation } from "./router";
import { RoomSessionProvider, useRoomSession, type AppUser } from "./room-session";

function JambandWordmark({ large = false }: { large?: boolean }) {
  return (
    <span className={`brand-wordmark${large ? " large" : ""}`} aria-hidden="true">
      <strong>J<span className="brand-letter-a">A<i /></span>MBAND</strong>
      <small>SOCIAL RADIO</small>
    </span>
  );
}

function MiniPlayer({ user }: { user: AppUser | null }) {
  const { route, navigate } = useNavigation();
  const session = useRoomSession();
  if (!session.room || route.name === "room") return null;
  const room = session.room;
  const track = room.nowPlaying;
  const isHost = user?.handle === room.hostHandle;
  return (
    <aside className="mini-player" aria-label="Active room player">
      <div className="mini-live"><i /> LIVE</div>
      <div className="track-art mini-art">{track?.coverUrl ? <img src={track.coverUrl} alt="" width="48" height="48" /> : "♪"}</div>
      <div className="mini-track"><strong>{track?.title || room.title}</strong><small>{track ? `${track.artist} · ${room.title}` : "Waiting for a song"}</small></div>
      {isHost && <button className="mini-control" aria-label={track?.isPlaying ? "Pause" : "Play"} onClick={() => void (async () => {
        await session.activateBrowserPlayer();
        await session.roomAction(`playback/${track?.isPlaying ? "pause" : "play"}`, {
          deviceId: session.browserPlayer.deviceId || undefined,
        });
      })()}>{track?.isPlaying ? "Ⅱ" : "▶"}</button>}
      <span className={`connection-chip ${session.connection}`}><i />{session.connection}</span>
      <button className="button primary mini-return" onClick={() => navigate(`/rooms/${encodeURIComponent(room.alias)}`)}>Return to room →</button>
    </aside>
  );
}

function AppHeader({ user, onLogin, onLogout }: { user: AppUser | null; onLogin: () => void; onLogout: () => void }) {
  const { route } = useNavigation();
  return (
    <header className="app-header">
      <AppLink href="/" className="brand" aria-label="Jamband home"><JambandWordmark /></AppLink>
      <nav aria-label="Main navigation">
        <AppLink href="/" className={route.name === "home" ? "active" : ""}>Room dial</AppLink>
        {user && <AppLink href={`/u/${user.handle}`} className={(route.name === "profile" || route.name === "profile-import") && route.handle === user.handle ? "active" : ""}>My profile</AppLink>}
      </nav>
      <div className="account-area">
        {user ? <><AppLink className="header-avatar" href={`/u/${user.handle}`}>{user.avatarUrl ? <img src={user.avatarUrl} width="32" height="32" alt="" /> : <span>{user.displayName.slice(0, 1)}</span>}<span><strong>{user.displayName}</strong><small>@{user.handle}</small></span></AppLink><button className="text-button" onClick={onLogout}>Sign out</button></> : <><span className="guest-label">BROWSING AS GUEST</span><button className="button spotify-button" onClick={onLogin}><i>●</i> Connect Spotify</button></>}
      </div>
    </header>
  );
}

function RoutedApp({ user, setUser, loading }: {
  user: AppUser | null;
  setUser: (user: AppUser | null) => void;
  loading: boolean;
}) {
  const { route } = useNavigation();
  const [authNotice, setAuthNotice] = React.useState("");
  const login = React.useCallback((pendingAction?: string) => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const params = new URLSearchParams({ returnTo });
    if (pendingAction) params.set("pendingAction", pendingAction);
    window.location.assign(`/api/auth/spotify/start?${params}`);
  }, []);
  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    setUser(null);
  };

  React.useEffect(() => {
    const url = new URL(window.location.href);
    const pending = url.searchParams.get("pendingFragmentAction");
    if (pending && user) setAuthNotice("Spotify connected. Your pending song action is ready to finish.");
  }, [user]);

  return (
    <RoomSessionProvider user={user}>
      <AppHeader user={user} onLogin={() => login()} onLogout={() => void logout()} />
      {authNotice && <div className="auth-return-notice"><span>✓</span>{authNotice}<button onClick={() => setAuthNotice("")}>×</button></div>}
      {loading ? <main className="app-loading"><JambandWordmark large /><p>Loading Jamband…</p></main> : route.name === "home" ? (
        <HomePage user={user} onRequireAuth={() => login("create-room")} />
      ) : route.name === "room" ? (
        <RoomPage roomRef={route.roomRef} invited={route.invited} user={user} onRequireAuth={login} />
      ) : route.name === "profile" ? (
        <ProfilePage handle={route.handle} user={user} onRequireAuth={login} />
      ) : route.name === "profile-import" ? (
        <SpotifyImportPage handle={route.handle} user={user} onReconnect={() => login("spotify-import")} />
      ) : (
        <main className="not-found page-shell"><span>404</span><h1>That frequency is off the dial.</h1><AppLink href="/">Back to Jamband</AppLink></main>
      )}
      <MiniPlayer user={user} />
    </RoomSessionProvider>
  );
}

export function CloudflareApp() {
  const [user, setUser] = React.useState<AppUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    void api<{ user: any }>("/api/me")
      .then((payload) => setUser({
        handle: payload.user.handle,
        displayName: payload.user.displayName || payload.user.display_name,
        avatarUrl: payload.user.avatarUrl || payload.user.avatar_url || null,
        subscriptionTier: payload.user.subscription_tier || null,
      }))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);
  return <NavigationProvider><RoutedApp user={user} setUser={setUser} loading={loading} /></NavigationProvider>;
}
